import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Issue, Status, Priority } from '../types';
import { STATUSES, PRIORITIES, USERS, LABELS } from '../store';
import { StatusIcon, PriorityIcon } from './icons';
import { X, Calendar, User as UserIcon, Tag, Link2, MoreHorizontal } from 'lucide-react';
import { format } from 'date-fns';
interface IssueDetailPanelProps {
  issue: Issue | null;
  onClose: () => void;
  onUpdate: (id: string, updates: Partial<Issue>) => void;
}
export function IssueDetailPanel({
  issue,
  onClose,
  onUpdate
}: IssueDetailPanelProps) {
  return <AnimatePresence data-id="element-83">
      {issue && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-slate-900/10 z-40 md:hidden backdrop-blur-[1px]" onClick={onClose} data-id="element-84" />
          <motion.div initial={{
        x: '100%',
        boxShadow: '-10px 0 30px rgba(0,0,0,0)'
      }} animate={{
        x: 0,
        boxShadow: '-10px 0 30px rgba(0,0,0,0.05)'
      }} exit={{
        x: '100%',
        boxShadow: '-10px 0 30px rgba(0,0,0,0)'
      }} transition={{
        type: 'spring',
        damping: 30,
        stiffness: 250
      }} className="fixed md:relative right-0 top-0 bottom-0 w-full md:w-[440px] bg-white border-l border-[#eef0f2] z-50 flex flex-col shadow-2xl md:shadow-none" data-id="element-85">
            {/* Header */}
            <div className="h-12 border-b border-[#eef0f2] flex items-center justify-between px-4 flex-shrink-0" data-id="element-86">
              <div className="flex items-center text-[13px] text-slate-500 font-medium" data-id="element-87">
                {issue.identifier}
              </div>
              <div className="flex items-center space-x-1" data-id="element-88">
                <button className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors" data-id="element-89">
                  <Link2 className="w-3.5 h-3.5" data-id="element-90" />
                </button>
                <button className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors" data-id="element-91">
                  <MoreHorizontal className="w-3.5 h-3.5" data-id="element-92" />
                </button>
                <div className="w-px h-3.5 bg-slate-200 mx-1" data-id="element-93"></div>
                <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors" data-id="element-94">
                  <X className="w-3.5 h-3.5" data-id="element-95" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6" data-id="element-96">
              <input type="text" value={issue.title} onChange={e => onUpdate(issue.id, {
            title: e.target.value
          })} className="w-full text-[18px] font-semibold text-slate-900 border-none outline-none focus:ring-0 p-0 mb-4 bg-transparent placeholder-slate-300" placeholder="Issue title" data-id="element-97" />

              <textarea value={issue.description} onChange={e => onUpdate(issue.id, {
            description: e.target.value
          })} className="w-full text-[14px] text-slate-600 border-none outline-none focus:ring-0 p-0 min-h-[100px] resize-none bg-transparent mb-8 placeholder-slate-300 leading-relaxed" placeholder="Add a description..." data-id="element-98" />

              {/* Properties */}
              <div className="space-y-4 border-t border-[#eef0f2] pt-6" data-id="element-99">
                <h4 className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-4" data-id="element-100">
                  Properties
                </h4>

                <div className="grid grid-cols-3 gap-4 items-center" data-id="element-101">
                  <div className="text-[13px] text-slate-500" data-id="element-102">Status</div>
                  <div className="col-span-2" data-id="element-103">
                    <select value={issue.status} onChange={e => onUpdate(issue.id, {
                  status: e.target.value as Status
                })} className="w-full text-[13px] border border-transparent hover:border-slate-200 rounded px-2 py-1.5 bg-transparent hover:bg-slate-50 focus:bg-white focus:outline-none focus:border-[#5e6ad2] focus:ring-1 focus:ring-[#5e6ad2] transition-colors cursor-pointer" data-id="element-104">
                      {STATUSES.map(s => <option key={s.id} value={s.id} data-id="element-105">
                          {s.label}
                        </option>)}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 items-center" data-id="element-106">
                  <div className="text-[13px] text-slate-500" data-id="element-107">Priority</div>
                  <div className="col-span-2" data-id="element-108">
                    <select value={issue.priority} onChange={e => onUpdate(issue.id, {
                  priority: e.target.value as Priority
                })} className="w-full text-[13px] border border-transparent hover:border-slate-200 rounded px-2 py-1.5 bg-transparent hover:bg-slate-50 focus:bg-white focus:outline-none focus:border-[#5e6ad2] focus:ring-1 focus:ring-[#5e6ad2] transition-colors cursor-pointer" data-id="element-109">
                      {PRIORITIES.map(p => <option key={p.id} value={p.id} data-id="element-110">
                          {p.label}
                        </option>)}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 items-center" data-id="element-111">
                  <div className="text-[13px] text-slate-500" data-id="element-112">Assignee</div>
                  <div className="col-span-2" data-id="element-113">
                    <select value={issue.assigneeId || ''} onChange={e => onUpdate(issue.id, {
                  assigneeId: e.target.value || undefined
                })} className="w-full text-[13px] border border-transparent hover:border-slate-200 rounded px-2 py-1.5 bg-transparent hover:bg-slate-50 focus:bg-white focus:outline-none focus:border-[#5e6ad2] focus:ring-1 focus:ring-[#5e6ad2] transition-colors cursor-pointer" data-id="element-114">
                      <option value="" data-id="element-115">Unassigned</option>
                      {USERS.map(u => <option key={u.id} value={u.id} data-id="element-116">
                          {u.name}
                        </option>)}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 items-start" data-id="element-117">
                  <div className="text-[13px] text-slate-500 mt-1.5" data-id="element-118">
                    Labels
                  </div>
                  <div className="col-span-2 flex flex-wrap gap-1.5" data-id="element-119">
                    {LABELS.map(label => {
                  const isSelected = issue.labelIds.includes(label.id);
                  return <button key={label.id} onClick={() => {
                    const newLabels = isSelected ? issue.labelIds.filter(id => id !== label.id) : [...issue.labelIds, label.id];
                    onUpdate(issue.id, {
                      labelIds: newLabels
                    });
                  }} className={`flex items-center px-2 py-1 rounded text-[11px] font-medium border transition-colors ${isSelected ? 'border-[#5e6ad2]/30 bg-[#5e6ad2]/10 text-[#5e6ad2]' : 'border-slate-200/60 bg-slate-50/50 text-slate-600 hover:bg-slate-100'}`} data-id="element-120">
                          <div className="w-1.5 h-1.5 rounded-full mr-1.5" style={{
                      backgroundColor: label.color
                    }} data-id="element-121" />
                          {label.name}
                        </button>;
                })}
                  </div>
                </div>
              </div>

              {/* Activity Placeholder */}
              <div className="mt-8 border-t border-[#eef0f2] pt-6" data-id="element-122">
                <h4 className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-4" data-id="element-123">
                  Activity
                </h4>
                <div className="flex items-start space-x-3" data-id="element-124">
                  <div className="w-6 h-6 rounded-full bg-[#f7f8f9] border border-slate-200 flex items-center justify-center flex-shrink-0" data-id="element-125">
                    <UserIcon className="w-3 h-3 text-slate-400" data-id="element-126" />
                  </div>
                  <div className="flex-1 border border-[#eef0f2] rounded-lg p-3 shadow-sm bg-white" data-id="element-127">
                    <p className="text-[13px] text-slate-400" data-id="element-128">
                      Leave a comment...
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}